import $ from 'jquery';
import { isString, wrap, defaults } from 'underscore';
import Backbone from 'backbone';
import { SimpleForm } from '@bingads-webui/simple-form';
import { fromLegacyTimeZone } from '@bingads-webui-universal/time-zone-converter';
import template from './template.pug';
import { getSupportedLanguages, getLanguageCode } from '../countdown-language';
import { DatePickerView } from './date-picker-view';
import { formatCountdown } from '../utils';

const getTimeZoneText = (timeZoneId, i18n) => {
  const text = i18n.getString(`TimeZone_${fromLegacyTimeZone(Number(timeZoneId))}`);
  return isString(text) ? text.replace(/\{0\}\s*\{1\}\s*/, '') : null;
};

/**
 * Get Countdown editor form schema
 * @param {Object} i18n - i18n
 * @param {Object} config - UI config settings
 * @returns {Array} supported langauges with format of [{codes, displayName, maxLength}]
 */
const getSchema = (i18n, config) => ({
  name: 'Countdown',
  type: 'object',
  properties: {
    date: {
      type: 'string',
      pattern: config.isResponsiveSearchAd ?
        '^[0-9]{4}-([1-9]|0[1-9]|1[0-2])-([1-9]|0[1-9]|[1-2][0-9]|3[0-1])$' :
        '^[0-9]{4}/([1-9]|0[1-9]|1[0-2])/([1-9]|0[1-9]|[1-2][0-9]|3[0-1])$',
    },
    time: {
      type: 'string',
      pattern: '^(?:((?:[0-1][0-9]|2[0-3]):(?:[0-5][0-9]):(?:[0-5][0-9]))|)$',
    },
    days: {
      type: 'integer',
      minimum: 1,
      maximum: 999,
    },
    isGlobal: {
      type: 'boolean',
    },
    language: {
      type: 'string',
      enum: getSupportedLanguages(i18n, config),
    },
  },
  required: ['date', 'days', 'language'],
});

/**
 * Countdown editor form fields
 */

const getFields = (i18n, config) => [
  {
    property: 'date',
    placeholder: 'yyyy/MM/dd',
    label: i18n.getString('CountdownEditor_CountdownEnds_Label'),
    control: DatePickerView,
    i18n,
    config,
    culture: config.get('culture') || 'en-US',
    validators: {
      pattern: {
        message: i18n.getString('CountdownEditor_CountdownEnds_PatternValidationMessage'),
      },
      required: {
        message: i18n.getString('CountdownEditor_CountdownEnds_RequiredValidationMessage'),
      },
    },
  },
  {
    property: 'time',
    placeholder: 'HH:mm:ss',
    id: 'CountdownEditor-CountdownEnds',
    label: i18n.getString('CountdownEditor_CountdownEndsTime_Label'),
    showLabel: false,
    validators: {
      pattern: {
        message: i18n.getString('CountdownEditor_CountdownEndsTime_PatternValidationMessage'),
      },
    },
  },
  {
    property: 'days',
    label: i18n.getString('CountdownEditor_CountdownStarts_Label'),
    id: 'CountdownEditor-CountdownStarts',
    validators: {
      type: {
        message: i18n.getString('CountdownEditor_CountdownStarts_TypeValidationMessage'),
      },
      range: {
        message: i18n.getString('CountdownEditor_CountdownStarts_RangeValidationMessage'),
      },
      required: {
        message: i18n.getString('CountdownEditor_CountdownStarts_RequiredValidationMessage'),
      },
      decimalLength: {
        message: i18n.getString('CountdownEditor_CountdownStarts_TypeValidationMessage'),
      },
    },
  },
  {
    property: 'isGlobal',
    control: 'radios',
    valueMessages: {
      true: `${i18n.getString('CountdownEditor_AccountTimeZone_Label')} ${getTimeZoneText(config.get('AccountTimeZone'), i18n)}`,
      false: i18n.getString('CountdownEditor_AdViewersTimeZone_Label'),
    },
    label: i18n.getString('CountdownEditor_TimeZone_Label'),
    id: 'CountdownEditor-TimeZone',
  },
  {
    property: 'language',
    label: i18n.getString('CountdownEditor_Language_Label'),
    id: 'CountdownEditor-Language',
  },
];

/**
 * Countdown editor view
 */
export class CountdownEditor extends Backbone.View {
  get className() {
    return 'countdownEditor';
  }

  initialize({
    updateAndCloseCallback,
    options,
    i18n,
    config,
    target,
    adType,
  }) {
    this.updateAndCloseCallback = updateAndCloseCallback;
    this.config = config;
    this.target = target;
    this.config.isResponsiveSearchAd = adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd';

    const model = {
      date: options.date,
      time: options.time,
      days: options.days,
      isGlobal: options.type === 'global_countdown',
      language: options.language ? options.language.displayName : '',
    };

    this.form = new SimpleForm({
      i18n,
      schema: getSchema(i18n, config),
      fields: getFields(i18n, config),
      model,
      template: wrap(
        template,
        (func, params) => func(defaults(
          params,
          { i18n, showLanguage: !config.isResponsiveSearchAd }
        ))
      ),
    });

    this.form.on('save', (newModel) => {
      const prefix = target.value.substring(0, options.startIndex);
      const suffix = target.value.substring(options.endIndex);

      const newValue = formatCountdown(
        prefix,
        newModel.isGlobal ? 'global_countdown' : 'countdown',
        newModel.date,
        newModel.time.length === 0 ? '00:00:00' : newModel.time,
        getLanguageCode(newModel.language, i18n, config),
        newModel.days,
        suffix,
        this.config.isResponsiveSearchAd
      );

      const cursorPosition = newValue.length - suffix.length;
      $('.k-calendar-container').remove();
      this.updateAndCloseCallback({ newValue, cursorPosition, isSelectedByUser: true });
    });

    this.form.on('cancel', () => {
      $('.k-calendar-container').remove();
      this.updateAndCloseCallback({ isCancel: true });
    });

    this.$el.attr('role', 'dialog');

    return this;
  }

  render() {
    this.$el.append(this.form.render().el);

    return this;
  }
}
